"use client";

import type { NavDecorationProps } from "@/components/decorations/types";
import { getActiveDecorationSet } from "@/lib/utils/decorations";
import { getDecorationSet } from "./registry";

/**
 * ActiveNavDecorations
 *
 * Renders the active navigation decorations based on NEXT_PUBLIC_ACTIVE_DECORATIONS.
 * Returns null if no decorations are enabled or if the active set has no nav component.
 *
 * @param placement - Where to place the nav decoration ("left" or "right")
 * @example
 * ```tsx
 * <ActiveNavDecorations placement="left" />
 * <ActiveNavDecorations placement="right" />
 * ```
 */
export function ActiveNavDecorations({ placement }: NavDecorationProps) {
  try {
    const activeSet = getActiveDecorationSet();

    if (!activeSet) {
      return null;
    }

    const decorationSet = getDecorationSet(activeSet);

    if (!decorationSet || !decorationSet.nav) {
      return null;
    }

    const NavComponent = decorationSet.nav;
    return <NavComponent placement={placement} />;
  } catch (error) {
    // Log error in development, fail silently in production
    if (process.env.NODE_ENV === "development") {
      console.error("[Decorations] Error rendering nav decorations:", error);
    }
    return null;
  }
}
